import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import menuEngineeringService, { MenuEngineeringItem, MenuEngineeringReport } from '../../../../services/menuEngineeringService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, RefreshCw, Loader2, Star, TrendingDown, HelpCircle, Zap } from 'lucide-react';
import '../RestaurantPage.css';

const CLASSIFICATION_ICONS: Record<string, React.ReactNode> = {
  Star: <Star size={14} />,
  Plowhorse: <Zap size={14} />,
  Puzzle: <HelpCircle size={14} />,
  Dog: <TrendingDown size={14} />,
};
const CLASSIFICATION_KEYS = ['Star', 'Plowhorse', 'Puzzle', 'Dog'] as const;

const MenuEngineeringReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [report, setReport] = useState<MenuEngineeringReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterClass, setFilterClass] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await menuEngineeringService.getReport(startDate || undefined, endDate || undefined);
      const d = (res.data as any)?.data ?? res.data;
      setReport(d);
    } catch {
      setError(t('restaurant.menuEngineeringReport') + ' — ' + t('restaurant.menuEngineering.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, t]);

  const visibleItems: MenuEngineeringItem[] = report
    ? (filterClass ? report.items.filter(i => i.classification === filterClass) : report.items)
    : [];

  return (
    <Layout>
      <div className="rest-list-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button className="ims-back-btn" onClick={() => navigate('/restaurant/menu-items')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('restaurant.menuEngineeringReport')}</div>
          </div>
          <div className="ims-top-bar-right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="date" className="ims-form-control" style={{ height: '34px', fontSize: '13px', width: '140px' }}
              value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span style={{ fontSize: '13px', color: 'var(--ims-text-sub)' }}>to</span>
            <input type="date" className="ims-form-control" style={{ height: '34px', fontSize: '13px', width: '140px' }}
              value={endDate} onChange={e => setEndDate(e.target.value)} />
            <button className="ims-btn ims-btn-primary" onClick={loadReport} disabled={loading}>
              {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
              {loading ? t('common.loading') : t('restaurant.menuEngineering.runReport')}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginTop: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {report && (
          <>
            {/* Summary cards */}
            <div className="rest-me-summary-grid">
              <div className="rest-me-summary-card">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.totalItems')}</div>
                <div className="rest-me-summary-value">{report.totalItems}</div>
              </div>
              <div className="rest-me-summary-card rest-me-summary-card-Star">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.stars')}</div>
                <div className="rest-me-summary-value rest-me-summary-value-Star">{report.stars}</div>
              </div>
              <div className="rest-me-summary-card rest-me-summary-card-Plowhorse">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.plowhorses')}</div>
                <div className="rest-me-summary-value rest-me-summary-value-Plowhorse">{report.plowhorses}</div>
              </div>
              <div className="rest-me-summary-card rest-me-summary-card-Puzzle">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.puzzles')}</div>
                <div className="rest-me-summary-value rest-me-summary-value-Puzzle">{report.puzzles}</div>
              </div>
              <div className="rest-me-summary-card rest-me-summary-card-Dog">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.dogs')}</div>
                <div className="rest-me-summary-value rest-me-summary-value-Dog">{report.dogs}</div>
              </div>
              <div className="rest-me-summary-card">
                <div className="rest-me-summary-label">{t('restaurant.menuEngineering.avgFoodCostPercent')}</div>
                <div className="rest-me-summary-value">{Number(report.averageFoodCostPercent).toFixed(1)}%</div>
              </div>
            </div>

            {/* Legend / filter */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button
                className={`rest-me-filter-btn ${filterClass === '' ? 'rest-me-filter-active-all' : ''}`}
                onClick={() => setFilterClass('')}>{t('restaurant.menuEngineering.filterAll')}</button>
              {CLASSIFICATION_KEYS.map(key => (
                <button key={key}
                  className={`rest-me-filter-btn ${filterClass === key ? `rest-me-filter-active-${key}` : ''}`}
                  onClick={() => setFilterClass(filterClass === key ? '' : key)}>
                  {CLASSIFICATION_ICONS[key]}
                  {t(`restaurant.menuEngineering.classifications.${key}.label`)} — {t(`restaurant.menuEngineering.classifications.${key}.desc`)}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="ims-card" style={{ marginTop: '1rem' }}>
              <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                {visibleItems.length === 0 ? (
                  <div className="rest-data-table-empty">
                    {t('restaurant.menuEngineering.noData')}
                  </div>
                ) : (
                  <table className="rest-data-table">
                    <thead>
                      <tr>
                        {[
                          t('restaurant.menuEngineering.columns.item'),
                          t('restaurant.menuEngineering.columns.category'),
                          t('restaurant.menuEngineering.columns.classification'),
                          t('restaurant.menuEngineering.columns.qtySold'),
                          t('restaurant.menuEngineering.columns.revenue'),
                          t('restaurant.menuEngineering.columns.foodCostPerUnit'),
                          t('restaurant.menuEngineering.columns.costSource'),
                          t('restaurant.menuEngineering.columns.totalFoodCost'),
                          t('restaurant.menuEngineering.columns.contributionMargin'),
                          t('restaurant.menuEngineering.columns.foodCostPercent'),
                          t('restaurant.menuEngineering.columns.popularity'),
                        ].map(h => <th key={h}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map(item => {
                        const fcPct = Number(item.foodCostPercent);
                        const fcClass = fcPct < 30 ? 'rest-me-fc-low' : fcPct < 40 ? 'rest-me-fc-mid' : 'rest-me-fc-high';
                        return (
                          <tr key={item.menuItemId}>
                            <td style={{ fontWeight: 500 }}>{item.itemName}</td>
                            <td style={{ color: 'var(--ims-text-sub)' }}>{item.categoryName || '—'}</td>
                            <td>
                              <span className={`rest-me-chip rest-me-chip-${item.classification}`}>
                                {CLASSIFICATION_ICONS[item.classification]}{t(`restaurant.menuEngineering.classifications.${item.classification}.label`, item.classification)}
                              </span>
                            </td>
                            <td style={{ fontWeight: 600 }}>{item.totalQtySold}</td>
                            <td>{fmt(Number(item.totalRevenue))}</td>
                            <td>{fmt(Number(item.foodCostPerUnit))}</td>
                            <td>
                              <span className={`rest-me-cost-source rest-me-cost-source-${item.costSource === 'Manual' ? 'Manual' : 'Recipe'}`}>
                                {item.costSource || t('restaurant.menuEngineering.costSourceRecipe')}
                              </span>
                            </td>
                            <td>{fmt(Number(item.totalFoodCost))}</td>
                            <td className={Number(item.contributionMargin) >= 0 ? 'rest-me-fc-low' : 'rest-me-fc-high'} style={{ fontWeight: 600 }}>
                              {fmt(Number(item.contributionMargin))}
                            </td>
                            <td className={fcClass} style={{ fontWeight: 600 }}>{fcPct.toFixed(1)}%</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div className="rest-me-popularity-track">
                                  <div className="rest-me-popularity-bar" style={{ width: `${Math.min(Number(item.popularityIndex) * 50, 100)}%` }} />
                                </div>
                                <span style={{ fontSize: '11px', color: 'var(--ims-text-sub)' }}>{Number(item.popularityIndex).toFixed(2)}x</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {!report && !loading && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: 'var(--ims-text-sub)' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#128202;</div>
            <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '8px' }}>{t('restaurant.menuEngineering.selectPrompt')}</div>
            <div style={{ fontSize: '13px' }}>{t('restaurant.menuEngineeringDesc')}</div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MenuEngineeringReportPage;
